import * as React from 'react';
import { compose, setDisplayName, withProps, withHandlers, withState, lifecycle } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { withRouter, RouteComponentProps } from 'react-router';
import { Report } from 'types';
import { State } from 'reducers';
import { getReport, isReportNew } from 'selectors/designerSelectors';
import { designerSetReport } from 'actions/designerActions';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { createNewReport } from 'logic/reports/createNewReport';
import { SectionLayout, scrollTo } from './SectionLayout';
import { GeneralSection } from './sections/general/GeneralSection';
import { CamerasSection } from './sections/cameras/CamerasSection';
import { ScheduleSection } from './sections/schedule/ScheduleSection';
import { DateRangeSection } from './sections/dateRange/DateRangeSection';
import { DistributionSection } from './sections/distribution/DistributionSection';
import { SensitivitySection } from './sections/sensitivity/SensitivitySection';
import { ZoneSelectionSection } from './sections/zoneSelection/ZoneSelectionSection';
import { LprFilterSection } from './sections/filter/lpr/LprFilterSection';
import { FaceRecognitionSection } from 'ui/pages/designer/sections/faceRec/FaceRecognitionSection';
import { GraphSection } from './sections/graph/GraphSection';
import { LineSection } from './sections/line/LineSection';
import { Button } from 'ui/shared/Button';
import { ButtonWithSpinner } from 'ui/shared/ButtonWithSpinner';
import { getTopBarVisible } from 'selectors/shellSelectors';
import { replace } from 'connected-react-router';
import { setTopBarVisibility } from 'actions/shellActions';
import { saveNewReport, updateReport } from 'api/reports';
import { reportAddedUpdated } from 'actions/reportActions';
import { TableLayoutSection } from 'ui/pages/designer/sections/tableLayout/TableLayoutSection';
import { hasFeature } from 'logic/reports/reportType';
import { FadeTranslate } from 'ui/transitions';
import { ErrorMessage } from 'ui/shared/ErrorMessage';

import './DesignerPage.css';

export type OwnProps = {
    visible: boolean;
};

const errorFadeFrom = {
    x: '100%',
    y: '.33em',
};
const errorFadeTo = {
    x: '0%',
    y: '.33em',
};
const errorStyle = {
    display: 'inline',
};

export const RawDesignerPage = ({
    visible,
    report,
    onChange,
    settings,
    isSaving,
    error,
    showError,
    handleChangeSettings,
    handleClickSave,
    handleClickCancel,
    ...extraProps
}: Props) =>
    <div id="designer-page" className="abs page" style={ extraProps.styles.Workspace }>
        <h1>
            { extraProps.t('6.1241.0.27') }
        </h1>
        <SectionLayout report={ report }>{ [
            {
                key: '6.1241.0.16',
                component: <GeneralSection report={ report } onChange={ onChange }/>,
            },
            {
                key: hasFeature(report.reportType, 'hasExitDevices')
                     ? 'Reports_EntranceCamera_Plural'
                     : hasFeature(report.reportType, 'singleCamera')
                        ? '184' // Camera
                        : '1066', // Cameras
                component: <CamerasSection settings={ settings } onChange={ handleChangeSettings } allowMultiple={ report.reportType !== 'HeatMap' }/>,
                visible: ({ report }) => !hasFeature(report.reportType, 'noDevices'),
            },
            {
                key: 'Reports_ExitCamera_Plural',
                component: <CamerasSection settings={ settings } onChange={ handleChangeSettings } allowMultiple={ report.reportType !== 'HeatMap' } isExit={ true }/>, // tslint:disable-line:max-line-length
                visible: ({ report }) => hasFeature(report.reportType, 'hasExitDevices'),
            },
            {
                key: 'Plugins_VE420_DisplayName',
                component: <FaceRecognitionSection reportSettings={ settings } onChange={ handleChangeSettings }/>,
                visible: ({ report }) => hasFeature(report.reportType, 'hasExitDevices'),
            },
            {
                key: 'VE420_FaceEnrollment_Filter',
                component: <LprFilterSection reportSettings={ settings } onChange={ handleChangeSettings }/>,
                visible: ({ report }) => hasFeature(report.reportType, 'hasFilter'),
            },
            {
                key: '6.1241.0.18',
                component: <LineSection reportSettings={ settings } onChange={ handleChangeSettings }/>,
                visible: ({ report }) => hasFeature(report.reportType, 'hasLine'),
                height: () => settings.devices.length > 0 ? '51em' : '8em',
            },
            {
                key: 'WC_REPORT_SECTION_ZONE_SELECTION',
                component: <ZoneSelectionSection reportSettings={ settings } onChange={ handleChangeSettings }/>,
                visible: ({ report }) => hasFeature(report.reportType, 'hasHeatMap'),
                height: () => '49em',
            },
            {
                key: '2827',
                component: <SensitivitySection reportSettings={ settings } onChange={ handleChangeSettings }/>,
                visible: ({ report }) => hasFeature(report.reportType, 'hasSensitivity'),
                height: () => '17em',
            },
            {
                key: '6.1237.3244',
                component: <DateRangeSection reportType={ report.reportType } reportSettings={ settings } onChange={ handleChangeSettings } />,
            },
            {
                key: '307',
                component: <ScheduleSection reportType={ report.reportType } reportSettings={ settings } onChange={ handleChangeSettings }/>,
            },
            {
                key: 'Distribution',
                component: <DistributionSection reportId={ report.reportId } reportType={ report.reportType }reportName={ report.name } reportSettings={ settings } onChange={ handleChangeSettings }/>, // tslint:disable-line:max-line-length
            },
            {
                key: '6.1241.0.28',
                component: <GraphSection reportType={ report.reportType } reportSettings={ settings } onChange={ handleChangeSettings }/>,
                visible: ({ report }) => !hasFeature(report.reportType, 'noOutputGraph'),
            },
            {
                key: 'WC_REPORT_SECTION_TABLE_LAYOUT',
                component: <TableLayoutSection reportType={ report.reportType } reportSettings={ settings } onChange={ handleChangeSettings }/>,
            },
        ] }</SectionLayout>
        <footer id="designer-page-footer">
            <FadeTranslate
                visible={ showError }
                duration={ 400 }
                from={ errorFadeFrom }
                to={ errorFadeTo }
            >
                <ErrorMessage
                    text={ error }
                    style={ errorStyle }
                />
            </FadeTranslate>
            <div id="edit-report-buttons" style={ extraProps.styles.Workspace }>
                <ButtonWithSpinner
                    id="edit-report-save-button"
                    textKey="6.1237.3101"
                    onClick={ handleClickSave }
                    spinning={ isSaving }
                />
                <Button
                    id="edit-report-cancel-button"
                    textKey="83"
                    onClick={ handleClickCancel }
                    disabled={ isSaving }
                />
            </div>
        </footer>
    </div>
;

type StateProps = {
    isNew: boolean; // true if new or a copy of existing
    report: Report;
    embedded: boolean;
};
type DispProps = {
    onChange: (report: Report) => void;
    showTopBar: (visible: boolean) => void;
    goBack: () => void;
    onReportAddedUpdated: (report: Report) => void;
};
type RecomposeProps = {
    error: string | null;
    showError: boolean;
    isSaving: false;
    settings: ReportSettings;
    handleChangeSettings: ReportSettingsChangeHandler;
    handleClickSave: () => void;
    handleClickCancel: () => void;
};
type _RecomposeProps = RecomposeProps & {
    _setError: (error: string | null) => void;
    _setShowError: (b: boolean) => void;
    _setIsSaving: (s: boolean) => void;
    _initialEmbedded: boolean;
};

// It's okay to materialize current date/time here because this constant would only be used if the
// user navigates directly to the New Report URL. When the user navigates through the UI, the new
// report will be generated from editReportSaga.
// Mostly, this is just here to facilitate development of the Report Designer. (i.e. so I can jump
// right to the designer directly)
const newReport = createNewReport('', Date.now(), '', 'Alarms', false);

function validate(report: Readonly<Report>): boolean {
    const reportSettings = ReportSettings.fromXml(report.settings);
    
    // Don't allow report to be saved if the name is missing
    if (!report.name) {
        scrollTo('#designer-page-section-6_1241_0_16');
        return false;
    }

    // Validate devices (if the report has devices)
    if (!hasFeature(report.reportType, 'noDevices')) {
        if (reportSettings.devices.length === 0) {
            // Scroll to general rather than try and figure out which camera section to scroll to
            scrollTo('#designer-page-section-6_1241_0_16');
            return false;
        }
    }

    // Validate exit devices (and Face Recognition hours to run)
    if (hasFeature(report.reportType, 'hasExitDevices')) {
        if (reportSettings.exitDevices.length === 0) {
            scrollTo('#designer-page-section-Reports_ExitCamera_Plural');
            return false;
        }
        if (reportSettings.hourOptions.length === 0) {
            scrollTo('#designer-page-section-Plugins_VE420_DisplayName');
            return false;
        }
    }

    // Validate zones (if the report has heatMap)
    if (hasFeature(report.reportType, 'hasHeatMap')) {
        if (reportSettings.heatMapZones.length === 0) {
            scrollTo('#designer-page-section-WC_REPORT_SECTION_ZONE_SELECTION');
            return false;
        }
    }

    // Validate schedule start date and time
    if (reportSettings.scheduleEnabled && !reportSettings.scheduleRangeStart) {
        scrollTo('#designer-page-section-307');
        return false;
    }

    // Validate schedule end date and time
    if (reportSettings.scheduleEnabled && reportSettings.scheduleRangeEndType === 'on' && !reportSettings.scheduleRangeEnd) {
        scrollTo('#designer-page-section-307');
        return false;
    }

    // Validate email distribution emails
    if (reportSettings.emailDistributionEnabled && reportSettings.emails.length === 0) {
        scrollTo('#designer-page-section-Distribution');
        return false;
    }

    // Validate email distribution file formats
    if (reportSettings.emailDistributionEnabled && reportSettings.emailFileTypes.length === 0) {
        scrollTo('#designer-page-section-Distribution');
        return false;
    }

    // Validate file distribution file formats
    if (reportSettings.fileDistributionEnabled && reportSettings.saveFileTypes.length === 0) {
        scrollTo('#save-report-file');
        return false;
    }

    return true;
}

const mapStateToProps = (state: State) => ({
    isNew: isReportNew(state),
    report: getReport(state) || newReport,
    embedded: !getTopBarVisible(state),
});

const mapDispatchToProps = (dispatch: Function, { location }: Props) => ({
    onChange: (report: Report) => dispatch(designerSetReport(report, {})),
    showTopBar: (visible: boolean) => dispatch(setTopBarVisibility(visible)),
    goBack: () => {
        // Remove the report ID (and anything following it) from our path to get the report list path
        let { pathname } = location;
        const pathSegments = pathname.split('/');
        pathname = pathSegments.slice(0, 3).join('/');
        dispatch(replace(pathname));
    },
    onReportAddedUpdated: (report: Report) => dispatch(reportAddedUpdated(report)),
});

type Props = OwnProps & StateProps & DispProps & RecomposeProps & OemProps & TranslateProps & RouteComponentProps<{}>;

export const DesignerPage = compose<Props, OwnProps>(
    setDisplayName('DesignerPage'),
    skin,
    translate(),
    withRouter,
    connect<StateProps, DispProps, {}>(mapStateToProps, mapDispatchToProps),
    withProps(({ report, styles }: StateProps & DispProps & OemProps) => ({
        settings: ReportSettings.fromXml(report!.settings),
    })),
    withState('showError', '_setShowError', false),
    withState('error', '_setError', null),
    withState('isSaving', '_setIsSaving', false),
    withState('_initialEmbedded', '__not_used', ({ embedded }) => embedded),
    withHandlers<StateProps & DispProps & _RecomposeProps & TranslateProps, Partial<RecomposeProps>>({
        handleChangeSettings: ({ report, settings, onChange }) => (fn: (reportSettings: Readonly<ReportSettings>) => Readonly<ReportSettings>) => {
            const newSettings = fn(settings);
            onChange({
                ...report!,
                scheduled: newSettings.scheduleEnabled,
                settings: newSettings.toXml(),
            });
        },
        handleClickSave: ({ report, isNew, goBack, _setIsSaving, _setShowError, _setError, onReportAddedUpdated, t }) => async () => {
            if (!validate(report)) {
                return;
            }

            _setIsSaving(true);
            _setShowError(false);
            try {
                if (isNew) {
                    const reportId = await saveNewReport(report);
                    onReportAddedUpdated({ ...report, reportId });
                }
                else {
                    await updateReport(report);
                    onReportAddedUpdated(report);
                }
                goBack();
            }
            catch (err) {
                // Show the error for four seconds
                const message = t(err.message);
                _setError(message);
                _setShowError(true);
                setTimeout(() => _setShowError(false), 4000);
            }
            finally {
                _setIsSaving(false);
            }
        },
        handleClickCancel: ({ goBack }) => () => {
            goBack();
        },
    }),
    lifecycle<Props & _RecomposeProps, Props & _RecomposeProps>({
        componentWillMount() {
            // If this page is starting visible then
            if (this.props.visible) {
                // Hide the top bar (don't want user using it to navigate away)
                this.props.showTopBar(false);
            }
        },
        componentDidUpdate(oldProps) {
            const { visible, showTopBar } = this.props;

            // If this page was just made visible then
            if (!oldProps.visible && visible) {
                // Hide the top bar (don't want user using it to navigate away)
                showTopBar(false);
                scrollTo('#designer-page-section-6_1241_0_16'); // Scroll to General section
            }

            // Else if this page was just hidden then
            else if (oldProps.visible && !visible) {
                // Show the top bar if it was visible before (otherwise, keep it hidden)
                const { _initialEmbedded, embedded } = this.props;
                if (!_initialEmbedded && embedded) {
                    showTopBar(true);
                }
            }
        },
    }),
)(RawDesignerPage);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/DesignerPage.tsx